import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  updateSelectedTile,
  updateWallsManagment,
  updateWallsVisibility,
  updateEventsVisibility,
  updateThemesVisibility,
  updateParentLayout,
  updateLayout,
} from "../../../reducers/configurator/configuratorSlice";
import GoldenSwitch from "../../goldenSwitch/GoldenSwitch";
import { resetLayers } from "../../../reducers/configurator/utils";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useViewport } from "../../../helpers";
import LayoutsModal from "./LayoutsModal";
import { Add, Create, Help } from "@mui/icons-material";
import "./configurator-layout.css";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { gamesTypes } from "../utils/utils";

const PREFIX = "ConfiguratorLayouts";

const classes = {
  root: `${PREFIX}-root`,
  displayToggles: `${PREFIX}-displayToggles`,
  toggleBox: `${PREFIX}-toggleBox`,
  selectorSection: `${PREFIX}-selectorSection`,
  selectorSectionTitle: `${PREFIX}-selectorSectionTitle`,
  selectorSectionTitleText: `${PREFIX}-selectorSectionTitleText`,
  selectorSectionTitleToolTip: `${PREFIX}-selectorSectionTitleToolTip`,
  tooltipLinkedIcon: `${PREFIX}-tooltipLinkedIcon`,
  iconButtonStyle: `${PREFIX}-iconButtonStyle`,
};

const ConfiguratorLayoutsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      width: "100%",
      marginBottom: "1rem",
      padding: "0 1rem 0 2rem",
    },
    [`& .${classes.displayToggles}`]: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
      width: "100%",
      [theme.breakpoints.between("xs", "sm")]: {
        flexWrap: "wrap",
      },
    },
    [`& .${classes.toggleBox}`]: {
      [theme.breakpoints.down("sm")]: {
        flex: "50%",
      },
    },

    [`& .${classes.selectorSection}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginTop: "0.5rem",
    },
    [`& .${classes.selectorSectionTitle}`]: {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.5rem",
    },
    [`& .${classes.selectorSectionTitleText}`]: {
      textAlign: "center",
      margin: "0.25rem 0",
    },
    [`& .${classes.selectorSectionTitleToolTip}`]: {
      marginLeft: "0.25rem",
      [theme.breakpoints.down("md")]: {
        cursor: "pointer",
      },
    },
    [`& .${classes.tooltipLinkedIcon}`]: {
      width: "18px",
      height: "18px",
      transform: "translateY(-6px)",
    },
    [`& .${classes.iconButtonStyle}`]: {
      backgroundColor: theme.palette.primary.main,
      color: "#424242",
      "&:hover": {
        backgroundColor: "#424242",
        color: theme.palette.primary.main,
      },
    },
  };
});

const ConfiguratorLayouts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useViewport();

  const [selectLayout, setSelectLayout] = useState({
    open: false,
    isDelete: false,
    isModify: false,
  });

  const currentLayout = useSelector((state) => state.configurator.layout);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const parentLayoutsList = useSelector(
    (state) => state.configurator.parentLayoutsList
  );
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );
  const is3dView = useSelector((state) => state.configurator.is3dView);
  const areEventsHidden = useSelector(
    (state) => state.configurator?.areEventsHidden
  );
  const areThemesHidden = useSelector(
    (state) => state.configurator?.areThemesHidden
  );
  const areWallsHidden = useSelector(
    (state) => state.configurator?.areWallsHidden
  );
  const switchDisabled = useSelector(
    (state) => state?.configurator?.multiSelection?.active
  );
  const gridsKeysToDisplay = useSelector(
    (state) => state.configurator.gridsKeysToDisplay
  );

  const handleLayoutToggle = (newLayout) => {
    if (currentLayout === newLayout) dispatch(updateLayout("normal"));
    else dispatch(updateLayout(newLayout));
  };

  const handleCloseModal = () => {
    setSelectLayout({ open: false, isDelete: false, isModify: false });
  };

  return (
    <ConfiguratorLayoutsContainer>
      <div className={classes.root}>
        <div className={classes.displayToggles}>
          {/* Events Switch */}
          <Box className={classes.toggleBox}>
            <GoldenSwitch
              checked={currentLayout === "event" ? true : false}
              onChange={() => {
                handleLayoutToggle("event");
              }}
              disabled={switchDisabled}
              label={t("myExpariences.form.toggleEvents")}
            />
            {is3dView ? (
              <IconButton
                onClick={() => {
                  dispatch(updateEventsVisibility());
                }}
              >
                {areEventsHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            ) : null}
          </Box>

          {/* Themes switch */}
          <Box className={classes.toggleBox}>
            <GoldenSwitch
              checked={currentLayout === "theme" ? true : false}
              onChange={() => {
                handleLayoutToggle("theme");
              }}
              disabled={switchDisabled}
              label={t("myExpariences.form.toggleThemes")}
            />
            {is3dView ? (
              <IconButton
                onClick={() => {
                  dispatch(updateThemesVisibility());
                }}
              >
                {areThemesHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            ) : null}
          </Box>

          {/* Walls switch */}
          <Box className={classes.toggleBox}>
            <GoldenSwitch
              checked={currentLayout === "walls" ? true : false}
              onChange={() => {
                dispatch(updateWallsManagment(resetLayers.wallsManagement));
                dispatch(updateSelectedTile(null));
                handleLayoutToggle("walls");
              }}
              disabled={switchDisabled}
              label={t("myExpariences.form.toggleWalls")}
            />
            {is3dView ? (
              <IconButton
                onClick={() => {
                  dispatch(updateWallsVisibility());
                }}
              >
                {areWallsHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            ) : null}
          </Box>

          {/* 3d view switch */}
          {/* {width >= 768 && (
            <GoldenSwitch
              checked={is3dView}
              onChange={() => {
                dispatch(update3dView());
                handleLayoutToggle("normal");
              }}
              disabled={switchDisabled}
              label={t("myExpariences.form.toggle3dView")}
            />
          )} */}
        </div>
        <Box className={classes.selectorSection}>
          <Box
            sx={{
              width: "100%",
              maxWidth: "315px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FormControl disabled={switchDisabled} sx={{ width: "350px" }}>
              <Box className={classes.selectorSectionTitle}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "between",
                  }}
                >
                  <Typography
                    variant="body1"
                    className={classes.selectorSectionTitleText}
                  >
                    {t("configurator.createOrSelectLayout")}
                  </Typography>
                  {width >= 768 && (
                    <Tooltip
                      title={t("configurator.createOrSelectLayoutToolTip")}
                      arrow
                      placement="left"
                      className={classes.selectorSectionTitleToolTip}
                    >
                      <Help
                        color="primary"
                        className={classes.tooltipLinkedIcon}
                      />
                    </Tooltip>
                  )}
                </Box>
                <IconButton
                  color="primary"
                  size="small"
                  className={classes.iconButtonStyle}
                  onClick={() =>
                    setSelectLayout({
                      open: true,
                      isDelete: false,
                      isModify: false,
                    })
                  }
                >
                  <Add />
                </IconButton>
              </Box>
              <Select
                onChange={(e) => {
                  dispatch(updateSelectedTile(null));
                  dispatch(updateParentLayout(e.target.value));
                }}
                value={parentLayout}
                renderValue={(selected) => (
                  <Box>{`${selected} ${
                    gridsKeysToDisplay.length > 1
                      ? `+ ${gridsKeysToDisplay[1]}`
                      : ""
                  }`}</Box>
                )}
              >
                {parentLayoutsList.map((item) => (
                  <MenuItem value={item.value} key={item.key}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {parentLayout !== "default" ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "0.1rem",
                }}
              >
                {!gamesTypes.includes(parentLayout) ? (
                  <>
                    <IconButton
                      color="primary"
                      aria-label="remove layer"
                      component="span"
                      sx={{
                        width: "50px",
                        height: "50px",
                        transform: "translateY(-7px)",
                      }}
                      onClick={(e) => {
                        setSelectLayout({
                          open: true,
                          isDelete: true,
                          isModify: false,
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      aria-label="remove layer"
                      component="span"
                      sx={{
                        width: "50px",
                        height: "50px",
                        transform: "translateY(-7px)",
                      }}
                      disabled={parentedLayoutsList[parentLayout]?.length > 0}
                      onClick={(e) => {
                        setSelectLayout({
                          open: true,
                          isDelete: false,
                          isModify: true,
                        });
                      }}
                    >
                      <Create />
                    </IconButton>
                  </>
                ) : (
                  <Box sx={{ width: "50px", height: "50px" }} />
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Modal open={selectLayout.open} onClose={handleCloseModal}>
          <>
            <LayoutsModal
              closeModal={handleCloseModal}
              deleteMode={selectLayout.isDelete}
              modifyMode={selectLayout.isModify}
            />
          </>
        </Modal>
      </div>
    </ConfiguratorLayoutsContainer>
  );
};

export default ConfiguratorLayouts;
